import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { BRAND_NAME } from '../../config/branding';
import { getFriendlyAuthError } from '../../lib/firebaseErrors';

// Anti-enumeration generic message - kept in sync with the success path
// at handleSubmit line ~44. If Firebase ever surfaces a user-not-found
// error from sendPasswordReset (Firebase v9+ with email-enumeration
// protection ON returns success silently, but older configs or auth
// settings can still leak the code), we render this same generic
// message so the UI never reveals account existence.
const ANTI_ENUM_GENERIC =
  'If an account exists with this email, you will receive a password reset link. Please check your inbox.';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose, onSwitchToLogin }) => {
  const { sendPasswordReset, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);

    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }

    try {
      await sendPasswordReset(email);
      // Always show generic message to prevent email enumeration attacks
      setSuccessMessage(ANTI_ENUM_GENERIC);
      setEmail('');
    } catch (err: any) {
      // Special-case: a `user-not-found` error from a misconfigured
      // Firebase project would leak account existence. Treat it as
      // success with the same generic message instead.
      if (err?.code === 'auth/user-not-found') {
        setSuccessMessage(ANTI_ENUM_GENERIC);
        setEmail('');
        return;
      }
      setLocalError(
        getFriendlyAuthError(err?.code, 'Failed to send password reset email. Please try again.')
      );
    }
  };

  const handleClose = () => {
    setEmail('');
    setLocalError(null);
    setSuccessMessage(null);
    onClose();
  };

  const handleSwitchToLogin = () => {
    setEmail('');
    setLocalError(null);
    setSuccessMessage(null);
    onSwitchToLogin();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Theming contract (CLAUDE.md): modal backdrops use --overlay so
        // the scrim flips per theme (light vs dark). Hex fallback keeps
        // the prior 50% black overlay when the var doesn't resolve.
        backgroundColor: 'var(--overlay, rgba(0, 0, 0, 0.5))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: 'var(--tv-bg-primary)',
          padding: '32px',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '400px',
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid var(--tv-border-primary)',
        }}
      >
        {/* Branding Header */}
        <div className="text-center mb-4">
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo-app.png`}
            alt={BRAND_NAME}
            className="d-block mx-auto mb-2"
            style={{ width: 48, height: 48, objectFit: 'contain' }}
          />
          <h3 className="text-lg font-semibold text-text-primary mb-0.5">
            {BRAND_NAME}
          </h3>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: '600',
              color: 'var(--tv-text-primary)',
            }}
          >
            Reset Password
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--tv-text-secondary)',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {localError && (
          <div
            style={{
              backgroundColor: 'var(--tv-error-bg, rgba(244, 67, 54, 0.1))',
              color: 'var(--tv-error-text, #f44336)',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '0.875rem',
              border: '1px solid var(--tv-error-border, rgba(244, 67, 54, 0.2))',
            }}
          >
            {localError}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              backgroundColor: 'var(--tv-success-bg, rgba(34, 197, 94, 0.15))',
              color: 'var(--tv-success-text, #22c55e)',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '0.875rem',
              border: '1px solid var(--tv-success-border, rgba(34, 197, 94, 0.2))',
            }}
          >
            {successMessage}
          </div>
        )}

        {!successMessage && (
          <>
            <p
              style={{
                fontSize: '0.875rem',
                color: 'var(--tv-text-secondary)',
                marginBottom: '20px',
                lineHeight: '1.5',
              }}
            >
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'var(--tv-text-primary)',
                  }}
                >
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--tv-border-primary)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: 'var(--tv-bg-secondary)',
                    color: 'var(--tv-text-primary)',
                    boxSizing: 'border-box',
                  }}
                  placeholder="Enter your email"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--accent-primary)',
                  color: 'var(--on_primary)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  marginBottom: '16px',
                }}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}

        <div
          style={{
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'var(--tv-text-secondary)',
          }}
        >
          Remember your password?{' '}
          <button
            type="button"
            onClick={handleSwitchToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent-primary)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;